/**
 * Music Playlist Async Thunks
 *
 * Redux async actions for fetching playlist data using SWR pattern
 *
 * Responsibilities:
 * - Coordinate PlaylistFetchService for SWR fetching
 * - Implement fallback order: Network → Cache → Mock
 * - Track data source for Redux state
 * - Handle loading states
 *
 * Clean Architecture:
 * - Thunks are adapter layer - coordinate use-cases and Redux
 * - Fallback policy implemented in PlaylistFetchService (use-case)
 * - Mock data fallback is last-resort boundary concern
 *
 * Strangler Pattern:
 * - Feature flag controls delegation to clean architecture path
 * - When useCleanPlaylistFetch is enabled, delegates to cleanPlaylistThunks
 * - Legacy implementation preserved for gradual migration
 *
 * Follows Single Responsibility Principle: Only handles async coordination
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { Playlist, PlaylistDataSource } from '@/types/MusicPlaylist';
import { Logger } from '@/app/utils/logger';
import { DataFetchLogger } from '@/app/services/logging/DataFetchLogger';
import { mockPlaylist } from './mockPlaylist';
import {
  getPerformanceTimingService,
  PERFORMANCE_MARKS,
} from '@/app/services/performance/PerformanceTimingService';
import {
  PlaylistFetchService,
  createR2NetworkFetcher,
} from '@services/music/PlaylistFetchService';
import { getPlaylistCacheService } from '@services/music/PlaylistCacheService';
import { getR2MusicService } from '@services/music/R2MusicService';
import { isCleanPlaylistFetchEnabled } from '@/app/config/featureFlags';
import { cleanFetchPlaylist, cleanRefreshPlaylist } from './cleanPlaylistThunks';
import { ErrorHandlerService } from '@/app/services/error/ErrorHandlerService';

const LOG_NAMESPACE = 'music-playlist-thunk';

/**
 * Thunk payload returned to Redux
 */
export interface PlaylistFetchPayload {
  playlist: Playlist;
  dataSource: PlaylistDataSource;
  isStale: boolean;
}

/**
 * Create PlaylistFetchService instance
 * Lazily constructed to avoid import-time errors when env vars not set
 */
function createFetchService(): PlaylistFetchService | null {
  const r2BucketUrl = import.meta.env.VITE_R2_MUSIC_BUCKET_URL;

  if (!r2BucketUrl) {
    Logger.warn('VITE_R2_MUSIC_BUCKET_URL not configured', LOG_NAMESPACE);
    return null;
  }

  try {
    const r2Service = getR2MusicService();
    const cacheGateway = getPlaylistCacheService();

    const networkFetcher = createR2NetworkFetcher((etag?: string) =>
      r2Service.fetchPlaylistConditional(etag)
    );

    return new PlaylistFetchService({
      cacheGateway,
      networkFetcher,
    });
  } catch (error) {
    Logger.error(
      `Failed to create PlaylistFetchService: ${error instanceof Error ? error.message : String(error)}`,
      LOG_NAMESPACE
    );
    return null;
  }
}

/**
 * Fetch playlist with fallback order: Network → Cache → Mock
 *
 * Fallback Policy:
 * 1. First: Try network (R2) via PlaylistFetchService
 * 2. Second: Fall back to last-known-good cache (if R2 fails)
 * 3. Third: Fall back to mock data ONLY when no cache exists AND R2 fails
 *
 * Strangler Pattern:
 * When useCleanPlaylistFetch flag is enabled, delegates to clean architecture thunk.
 */
export const fetchPlaylistFromR2 = createAsyncThunk<
  PlaylistFetchPayload,
  void,
  { rejectValue: string }
>('musicPlaylist/fetchFromR2', async (_, { dispatch }) => {
  // Strangler pattern: check feature flag for clean architecture path
  if (isCleanPlaylistFetchEnabled()) {
    Logger.info('Using clean architecture path for playlist fetch', LOG_NAMESPACE);
    const result = await dispatch(cleanFetchPlaylist()).unwrap();
    return result;
  }

  // Legacy implementation below
  const startTime = performance.now();

  // Check if R2 URL is configured
  const r2BucketUrl = import.meta.env.VITE_R2_MUSIC_BUCKET_URL;

  if (!r2BucketUrl) {
    // No R2 configured - use mock data immediately
    const durationMs = Math.round(performance.now() - startTime);

    DataFetchLogger.logFetch({
      operation: 'playlist:thunk',
      source: 'mock',
      status: 'fallback',
      durationMs,
      fallbackReason: 'r2_url_not_configured',
      itemCount: mockPlaylist.tracks.length,
    });

    Logger.warn(
      'VITE_R2_MUSIC_BUCKET_URL not configured, using mock data',
      LOG_NAMESPACE
    );

    // Mark playlist ready for performance tracking
    getPerformanceTimingService().mark(PERFORMANCE_MARKS.PLAYLIST_READY);

    return {
      playlist: mockPlaylist,
      dataSource: 'mock' as PlaylistDataSource,
      isStale: false,
    };
  }

  // Create fetch service
  const fetchService = createFetchService();

  if (!fetchService) {
    // Service creation failed - use mock data
    const durationMs = Math.round(performance.now() - startTime);

    DataFetchLogger.logFetch({
      operation: 'playlist:thunk',
      source: 'mock',
      status: 'fallback',
      durationMs,
      fallbackReason: 'fetch_service_creation_failed',
      itemCount: mockPlaylist.tracks.length,
    });

    Logger.warn('PlaylistFetchService unavailable, using mock data', LOG_NAMESPACE);

    getPerformanceTimingService().mark(PERFORMANCE_MARKS.PLAYLIST_READY);

    return {
      playlist: mockPlaylist,
      dataSource: 'mock' as PlaylistDataSource,
      isStale: false,
    };
  }

  try {
    Logger.info('Fetching playlist via PlaylistFetchService', LOG_NAMESPACE);

    // Fetch using SWR pattern
    const result = await fetchService.fetch();
    const durationMs = Math.round(performance.now() - startTime);

    // Map FetchResultSource to PlaylistDataSource
    const dataSource: PlaylistDataSource = result.source;

    DataFetchLogger.logFetch({
      operation: 'playlist:thunk',
      source: result.source === 'network' ? 'r2' : result.source,
      status: 'success',
      durationMs,
      cacheHit: result.source === 'cache' || result.source === 'fallback',
      itemCount: result.data.tracks.length,
    });

    Logger.info(
      `Playlist loaded: ${result.data.tracks.length} tracks, source=${result.source}, isStale=${result.isStale}`,
      LOG_NAMESPACE
    );

    // Mark playlist ready for performance tracking
    getPerformanceTimingService().mark(PERFORMANCE_MARKS.PLAYLIST_READY);

    return {
      playlist: result.data,
      dataSource,
      isStale: result.isStale,
    };
  } catch (error) {
    // PlaylistFetchService throws when BOTH network AND cache fail
    // This is our last resort - use mock data
    const durationMs = Math.round(performance.now() - startTime);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    // Route error through centralized error pipeline
    ErrorHandlerService.getInstance().handleDataFetchError(error, 'r2', {
      operation: 'playlist:thunk',
      durationMs,
    });

    DataFetchLogger.logFetch({
      operation: 'playlist:thunk',
      source: 'mock',
      status: 'fallback',
      durationMs,
      fallbackReason: `all_sources_failed: ${errorMessage}`,
      itemCount: mockPlaylist.tracks.length,
    });

    Logger.error(
      `All data sources failed, using mock data. Error: ${errorMessage}`,
      LOG_NAMESPACE
    );

    // Mark playlist ready even for mock data fallback
    getPerformanceTimingService().mark(PERFORMANCE_MARKS.PLAYLIST_READY);

    return {
      playlist: mockPlaylist,
      dataSource: 'mock' as PlaylistDataSource,
      isStale: false,
    };
  }
});

/**
 * Refresh playlist (force network fetch, clears cache)
 *
 * Fallback Policy (same as fetch):
 * 1. First: Force network (R2) fetch
 * 2. Second: Fall back to cache (if network fails)
 * 3. Third: Fall back to mock data (if both fail)
 *
 * Strangler Pattern:
 * When useCleanPlaylistFetch flag is enabled, delegates to clean architecture thunk.
 */
export const refreshPlaylistFromR2 = createAsyncThunk<
  PlaylistFetchPayload,
  void,
  { rejectValue: string }
>('musicPlaylist/refreshFromR2', async (_, { dispatch }) => {
  // Strangler pattern: check feature flag for clean architecture path
  if (isCleanPlaylistFetchEnabled()) {
    Logger.info('Using clean architecture path for playlist refresh', LOG_NAMESPACE);
    const result = await dispatch(cleanRefreshPlaylist()).unwrap();
    return result;
  }

  // Legacy implementation below
  const startTime = performance.now();

  const r2BucketUrl = import.meta.env.VITE_R2_MUSIC_BUCKET_URL;

  if (!r2BucketUrl) {
    const durationMs = Math.round(performance.now() - startTime);

    DataFetchLogger.logFetch({
      operation: 'playlist:refresh',
      source: 'mock',
      status: 'fallback',
      durationMs,
      fallbackReason: 'r2_url_not_configured',
      itemCount: mockPlaylist.tracks.length,
    });

    Logger.warn(
      'VITE_R2_MUSIC_BUCKET_URL not configured, using mock data',
      LOG_NAMESPACE
    );

    return {
      playlist: mockPlaylist,
      dataSource: 'mock' as PlaylistDataSource,
      isStale: false,
    };
  }

  const fetchService = createFetchService();

  if (!fetchService) {
    const durationMs = Math.round(performance.now() - startTime);

    DataFetchLogger.logFetch({
      operation: 'playlist:refresh',
      source: 'mock',
      status: 'fallback',
      durationMs,
      fallbackReason: 'fetch_service_creation_failed',
      itemCount: mockPlaylist.tracks.length,
    });

    Logger.warn('PlaylistFetchService unavailable, using mock data', LOG_NAMESPACE);

    return {
      playlist: mockPlaylist,
      dataSource: 'mock' as PlaylistDataSource,
      isStale: false,
    };
  }

  try {
    Logger.info('Force refreshing playlist via PlaylistFetchService', LOG_NAMESPACE);

    // Force refresh (bypass cache, fetch from network)
    const result = await fetchService.forceRefresh();
    const durationMs = Math.round(performance.now() - startTime);

    const dataSource: PlaylistDataSource = result.source;

    DataFetchLogger.logFetch({
      operation: 'playlist:refresh',
      source: result.source === 'network' ? 'r2' : result.source,
      status: 'success',
      durationMs,
      itemCount: result.data.tracks.length,
    });

    Logger.info(
      `Playlist refreshed: ${result.data.tracks.length} tracks, source=${result.source}`,
      LOG_NAMESPACE
    );

    return {
      playlist: result.data,
      dataSource,
      isStale: result.isStale,
    };
  } catch (error) {
    const durationMs = Math.round(performance.now() - startTime);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    // Route error through centralized error pipeline
    ErrorHandlerService.getInstance().handleDataFetchError(error, 'r2', {
      operation: 'playlist:refresh',
      durationMs,
    });

    DataFetchLogger.logFetch({
      operation: 'playlist:refresh',
      source: 'mock',
      status: 'fallback',
      durationMs,
      fallbackReason: `refresh_failed: ${errorMessage}`,
      itemCount: mockPlaylist.tracks.length,
    });

    Logger.error(
      `Playlist refresh failed, using mock data. Error: ${errorMessage}`,
      LOG_NAMESPACE
    );

    return {
      playlist: mockPlaylist,
      dataSource: 'mock' as PlaylistDataSource,
      isStale: false,
    };
  }
});
